import { Box, Checkbox, Input } from "@chakra-ui/react";
import { useCallback } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { produce } from "immer";
import { useRecoilValue } from "recoil";
import useInflatedTabs, {
  PriceConfig,
} from "@/features/line-sheet-sets/helpers/use-inflated-tabs";
import {
  FlatProduct,
  FlatSheetKey,
  orderSheetIvaConditionFamily,
  parseOrderSheetIvaCondition,
} from "@/features/line-sheet-sets/helpers/sheet-state";

interface OrderSheetIvaCellProps
  extends ICellRendererParams<FlatProduct, FlatProduct["ivaValue"]> {
  sheetKey: FlatSheetKey;
  tabIndex: number;
  isEditable: boolean;
  // 컨펌 화면처럼 수수료를 반영해야 하는 경우 전달(없으면 미적용).
  priceConfig?: PriceConfig;
  onUpdatingInflatedProductComplete?: (isCompleted: boolean) => void;
}

// 주문서 상품 단위 IVA(적용 여부 + 계산식) 편집 셀.
// 상품당 3행 몰드: 0행 = IVA 토글(applyIva), 1행 = IVA 식(ivaValue), 2행 = 없음.
// DISCOUNT 컨디션에서만 노출(MARKUP은 IVA 무관).
export default function OrderSheetIvaCell({
  rowIndex,
  data,
  sheetKey,
  tabIndex,
  isEditable,
  priceConfig,
  onUpdatingInflatedProductComplete,
}: OrderSheetIvaCellProps) {
  const { getInflatedProductById, updateInflatedProduct } = useInflatedTabs();

  // Recoil 상태를 직접 구독 → 컨디션 타입(by) 변경 시 AG-Grid 변경감지에
  // 의존하지 않고도 즉시 재렌더된다. (이전엔 getInflatedProductById로 1회
  // 스냅샷만 읽어, 셀이 강제 리렌더되기 전까지 DISCOUNT 전환이 반영 안 됐음.)
  const serializedCondition = useRecoilValue(
    orderSheetIvaConditionFamily({
      key: sheetKey,
      tabIndex,
      productId: data?.id ?? -1,
    })
  );
  const condition = parseOrderSheetIvaCondition(serializedCondition);

  const isDiscount = condition?.by === "DISCOUNT";
  // isConditionFixed와 무관하게 IVA는 항상 수정 가능(수정 권한 isEditable만 확인).
  const disabled = !isEditable;
  const applyIva = condition?.applyIva !== false; // null/undefined → true
  const ivaValue = condition?.ivaValue ?? "/1.22";

  const patchCondition = useCallback(
    (patch: { applyIva?: boolean; ivaValue?: string }) => {
      if (!data) return;
      // 편집 시점의 최신 inflated product를 읽어 패치(스냅샷 읽기로 충분).
      const inflatedProduct = getInflatedProductById(sheetKey, tabIndex, data.id);
      if (!inflatedProduct) return;
      const next = produce(inflatedProduct, (draft) => {
        const target =
          draft.confirmedPriceAdjustedBy ?? draft.priceAdjustedBy;
        if (!target) return;
        if (patch.applyIva !== undefined) target.applyIva = patch.applyIva;
        if (patch.ivaValue !== undefined) target.ivaValue = patch.ivaValue;
      });
      updateInflatedProduct(sheetKey, tabIndex, next, priceConfig);
      onUpdatingInflatedProductComplete?.(true);
    },
    [
      data,
      getInflatedProductById,
      sheetKey,
      tabIndex,
      priceConfig,
      updateInflatedProduct,
      onUpdatingInflatedProductComplete,
    ]
  );

  if (!isDiscount) {
    // DISCOUNT가 아니면 IVA 무관 — 빈 칸 표시.
    return rowIndex % 3 === 1 ? (
      <Box fontSize={"11px"} color={"#C4C4C1"} textAlign={"center"}>
        -
      </Box>
    ) : null;
  }

  if (rowIndex % 3 === 0) {
    return (
      <Checkbox
        isChecked={applyIva}
        isDisabled={disabled}
        onChange={(e) => patchCondition({ applyIva: e.target.checked })}
      >
        <Box fontSize={"11px"}>IVA</Box>
      </Checkbox>
    );
  } else if (rowIndex % 3 === 1) {
    return (
      <Input
        width={"84px"}
        height={"22px"}
        minH={"0"}
        p={"0 6px"}
        fontSize={"12px"}
        textAlign={"center"}
        placeholder={"/1.22"}
        borderColor={"#D4D4D1"}
        value={ivaValue}
        isDisabled={disabled || !applyIva}
        onChange={(e) => patchCondition({ ivaValue: e.target.value })}
      />
    );
  }
  return null;
}
